// Maps the lowercase `artefact_type` enum value (what the API returns) to a
// human-readable label for the Artefacts page. Kept in sync with the set in
// apps/api/src/db/schema/artefacts.ts — when that enum grows, add an entry
// here so new types don't render as their raw snake_case identifier.
export const ARTEFACT_TYPE_LABELS: Record<string, string> = {
  prd:                   'Product Requirements Document',
  user_stories:          'User Stories',
  sad:                   'System Architecture Document',
  adr:                   'Architectural Decision Records',
  erd:                   'Entity Relationship Diagram',
  sprint_plan:           'Sprint Plan',
  sprint_backlog:        'Sprint Backlog',
  implementation_prompt: 'Implementation Prompt',
  dev_response:          'Development Response',
  sprint_contract:       'Sprint Contract',
  change_order:          'Change Order',
  code_review_report:    'Code Review Report',
  qa_report:             'QA Report',
  test_plan:             'Test Plan',
  deployment_runbook:    'Deployment Runbook',
  handoff_package:       'Handoff Package',
  analysis_report:       'Analysis Report',
  brownfield_assessment: 'Brownfield Assessment',
  calibration_report:    'Calibration Report',
  retro_report:          'Retrospective Report',
  amendment_request:     'Amendment Request',
  codebase_summary:      'Codebase Summary',
  user_journey_map:      'User Journey Map',
  screen_inventory:      'Screen Inventory',
  screen_spec:           'Screen Specifications',
  component_catalogue:   'Component Catalogue',
  design_tokens:         'Design Tokens',
  claude_design_bundle:  'Claude Design Bundle',
};

/**
 * Canonical display name for an artefact row in the Artefacts page.
 *
 * Strategy: prefer the canonical type label so the UI is consistent and
 * version-prefixes stay legible (e.g. "Product Requirements Document v1.0"
 * instead of "Phoenix Ad… v1.0"). Two exceptions keep useful custom titles:
 *
 *   - `handoff_package` — the title encodes the phase transition
 *     ("Phase 1 → Phase 2 Handoff Package"), which is more informative than
 *     the generic label.
 *   - Imported artefacts — the title is the user's original filename, which
 *     is what they need to recognise it. The caller signals this by passing
 *     `'imported'` as the type (see the Artefacts page call site).
 *
 * Unknown types fall back to the stored title and then to the raw type
 * string, so nothing renders as blank.
 */
export function getArtefactDisplayName(type: string, title?: string): string {
  if (type === 'imported') {
    return title || 'Imported Document';
  }
  if (type === 'handoff_package' && title) {
    return title;
  }
  return ARTEFACT_TYPE_LABELS[type] ?? title ?? type;
}
